import React from 'react';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';

import {
  ChangeMaterialsFiltersFunc,
  FetchMaterialsFilterFields,
  FetchMaterialsFilterMaterials,
  FetchMaterialsFilters,
  MaterialCategoryID
} from '../../../../../../materials/materialsTypes';
import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../categories/categoriesTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { BooleanFilterBadge } from '../../../../../../../helpers/filterBadges/BooleanFilterBadge';
import { MaterialsBrandsFilterBadge } from '../../../../../../materials/components/filterBadges/MaterialsBrandsFilterBadge';
import { MaterialsCategoriesFilterBadge } from './components/MaterialsCategoriesFilterBadge';

import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';

import { isEveryPropEmpty } from '../../../../../../../utils/isEveryPropEmpty';

import { words } from '../../../../../../../locales/keys';

const allFilterFields = [
  FetchMaterialsFilterFields.BRAND_ID,
  FetchMaterialsFilterFields.BLOCKED,
  FetchMaterialsFilterFields.FAVORITE,
  FetchMaterialsFilterFields.CURRENT_TEAM_FAVORITE,
  FetchMaterialsFilterFields.TEAM_ID_FAVORITE
];

interface ThreeDStockMaterialsFiltersAppliedFiltersProps {
  materialsFilters: FetchMaterialsFilters;
  changeMaterialsFilters: ChangeMaterialsFiltersFunc;
  filterMaterials: FetchMaterialsFilterMaterials;
  clearFilters?: () => void;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  defaultMaterialCategoryIds?: MaterialCategoryID[];
  defaultMaterialParentCategoryIds?: MaterialCategoryID[];
}

function ThreeDStockMaterialsFiltersAppliedFilters({
  materialsFilters,
  changeMaterialsFilters,
  filterMaterials,
  clearFilters,
  checkedCategories,
  setCheckedCategories,
  defaultMaterialCategoryIds,
  defaultMaterialParentCategoryIds
}: ThreeDStockMaterialsFiltersAppliedFiltersProps) {
  const withFilters =
    !isEveryPropEmpty(pick(materialsFilters, allFilterFields)) ||
    !isEmpty(checkedCategories);

  if (!withFilters) {
    return null;
  }

  return (
    <div className="p-4" id="applied-filters">
      <div className="flex flex-wrap -m-0.5">
        <MaterialsBrandsFilterBadge
          filters={materialsFilters}
          name={FetchMaterialsFilterFields.BRAND_ID}
          onRemoveFilter={filterMaterials}
        />
        <BooleanFilterBadge
          dataGa="materials-blocked-filter-badge-clear"
          filters={materialsFilters}
          i18nLabel={words.blocked}
          name={FetchMaterialsFilterFields.BLOCKED}
          onRemoveFilter={filterMaterials}
        />

        <BooleanFilterBadge
          dataGa="materials-favorites-filter-badge-clear"
          filters={materialsFilters}
          i18nLabel={words.favorites}
          name={FetchMaterialsFilterFields.FAVORITE}
          onRemoveFilter={filterMaterials}
        />

        <BooleanFilterBadge
          dataGa="materials-current-company-favorite-filter-badge-clear"
          filters={materialsFilters}
          i18nLabel={words.companyFavorites}
          name={FetchMaterialsFilterFields.CURRENT_TEAM_FAVORITE}
          onRemoveFilter={filterMaterials}
        />

        <BooleanFilterBadge
          dataGa="materials-company-favorite-filter-badge-clear"
          filters={materialsFilters}
          i18nLabel={words.companyFavorites}
          name={FetchMaterialsFilterFields.TEAM_ID_FAVORITE}
          onRemoveFilter={filterMaterials}
        />

        <MaterialsCategoriesFilterBadge
          changeMaterialsFilters={changeMaterialsFilters}
          setCheckedCategories={setCheckedCategories}
          checkedCategories={checkedCategories}
          defaultMaterialCategoryIds={defaultMaterialCategoryIds}
          defaultMaterialParentCategoryIds={defaultMaterialParentCategoryIds}
        />

        {withFilters && (
          <PureIconButtonHelper
            dataGa="materials-clear-filters-badge"
            className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base border border-gray-300 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 m-0.5"
            i18nText={words.clear}
            i18nTextClassName="inline"
            icon={IconsEnum.X}
            iconClassName="w-4 h-4"
            onClick={clearFilters}
          />
        )}
      </div>
    </div>
  );
}

export default ThreeDStockMaterialsFiltersAppliedFilters;
